import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'dark' | 'light' | 'auto';
export type ResolvedTheme = 'dark' | 'light';

interface ThemeState {
  theme: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemeMode) => void;
  _applyTheme: (theme: ThemeMode) => void;
}

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function resolveTheme(theme: ThemeMode): ResolvedTheme {
  return theme === 'auto' ? getSystemTheme() : theme;
}

function applyToDOM(resolved: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', resolved);

  // Update meta theme-color for mobile browsers
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', resolved === 'light' ? '#fafafa' : '#0A0A0A');
  }

  // Update Telegram Mini App colors
  const tg = (window as any).Telegram?.WebApp;
  if (tg) {
    const bgColor = resolved === 'light' ? '#fafafa' : '#0A0A0A';
    try { tg.setHeaderColor?.(bgColor); } catch { /* ignore */ }
    try { tg.setBackgroundColor?.(bgColor); } catch { /* ignore */ }
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'dark',
      resolvedTheme: 'dark',

      setTheme: (theme: ThemeMode) => {
        const resolved = resolveTheme(theme);
        applyToDOM(resolved);
        set({ theme, resolvedTheme: resolved });
      },

      _applyTheme: (theme: ThemeMode) => {
        const resolved = resolveTheme(theme);
        applyToDOM(resolved);
        set({ resolvedTheme: resolved });
      },
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => {
        return (state: ThemeState | undefined) => {
          if (state) {
            const resolved = resolveTheme(state.theme);
            applyToDOM(resolved);
            state.resolvedTheme = resolved;
          }
        };
      },
    }
  )
);
